/**
 * 🚀 [Phase v13.56 — 2026-07-22] Dialog upload contrat .docx.
 *
 * L'admin dépose un .docx (fichier avocat) → backend :
 *   - Stocke le .docx original en GridFS
 *   - Convertit .docx → PDF pixel-perfect (LibreOffice)
 *   - Convertit .docx → HTML sémantique (mammoth)
 *   - Crée un nouveau template ACTIF (désactive l'ancien du même type)
 * Puis on affiche à l&apos;admin le PDF pixel-perfect en preview, avant
 * qu'il/elle poursuive dans l'éditeur Tiptap pour placer les variables.
 */
import { useState, useRef, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Upload, FileText, CheckCircle, Loader2, AlertTriangle } from "lucide-react";

const API_URL = (typeof window !== "undefined" ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || "";
const MAX_SIZE_BYTES = 20 * 1024 * 1024;

export function UploadContractDialog({ open, onClose, typeTemplate, defaultTitre, onUploaded }) {
  const [file, setFile] = useState(null);
  const [titre, setTitre] = useState(defaultTitre || "");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  // Reset quand la dialog se rouvre
  useEffect(() => {
    if (open) {
      setFile(null);
      setTitre(defaultTitre || "");
      setUploading(false);
      setProgress(0);
      setResult(null);
      setPdfPreviewUrl(null);
    }
  }, [open, defaultTitre]);

  // Cleanup du blob URL quand on démonte / le remplace
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".docx")) {
      toast.error("Format non supporté — .docx uniquement");
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      toast.error("Fichier trop volumineux (> 20 Mo)");
      return;
    }
    setFile(f);
    setResult(null);
    setPdfPreviewUrl(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);

    // Progression fictive (conversion LibreOffice peut prendre quelques secondes)
    const progressInterval = setInterval(() => {
      setProgress(p => Math.min(90, p + 10));
    }, 500);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const url = `${API_URL}/api/contracts/templates/upload?type_template=${encodeURIComponent(typeTemplate)}` +
        (titre ? `&titre=${encodeURIComponent(titre)}` : "");
      const r = await axios.post(url, formData);
      clearInterval(progressInterval);
      setProgress(100);
      setResult(r.data);

      // Prévisualisation PDF pixel-perfect via endpoint dédié
      if (r.data.has_pdf_preview && r.data.template_id) {
        try {
          const pdfRes = await axios.get(
            `${API_URL}/api/contracts/templates/${r.data.template_id}/preview-pdf`,
            { responseType: "blob" }
          );
          const url2 = window.URL.createObjectURL(pdfRes.data);
          setPdfPreviewUrl(url2);
        } catch (e) {
          // PDF preview absent — dégradation propre
        }
      }

      toast.success("Contrat uploadé et converti");
    } catch (e) {
      clearInterval(progressInterval);
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Erreur upload : ${detail}`);
      setUploading(false);
      return;
    }
    setUploading(false);
  };

  const validate = () => {
    if (result && onUploaded) onUploaded(result);
    onClose();
  };

  const fileSizeKb = useMemo(() => file ? Math.round(file.size / 1024) : 0, [file]);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="upload-contract-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Uploader un contrat .docx
          </DialogTitle>
          <DialogDescription>
            Déposez votre fichier .docx (rédigé par la firme d&apos;avocats). La plateforme le convertira en PDF pixel-perfect + le rendra éditable pour placer les variables dynamiques.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!result && (
            <>
              {/* Dropzone */}
              <div
                onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
                onDragOver={e => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
                }`}
                data-testid="upload-dropzone"
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={e => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="upload-file-input"
                />
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="w-10 h-10 text-blue-500" />
                    <p className="font-medium text-slate-900" data-testid="upload-file-name">{file.name}</p>
                    <p className="text-xs text-slate-500">{fileSizeKb.toLocaleString("fr-CA")} Ko</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-10 h-10 text-slate-400" />
                    <p className="font-medium text-slate-700">Glissez votre fichier .docx ici</p>
                    <p className="text-xs text-slate-500">ou cliquez pour parcourir · Max 20 Mo</p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="upload-titre">Titre du contrat (optionnel)</Label>
                <Input
                  id="upload-titre"
                  value={titre}
                  onChange={e => setTitre(e.target.value)}
                  placeholder="Ex: Contrat de travail permanent — Peintre 2026 v2"
                  className="mt-1"
                  data-testid="upload-titre-input"
                />
              </div>

              {uploading && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600">Conversion en cours… (5–30s)</p>
                  <Progress value={progress} data-testid="upload-progress" />
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="upload-result">
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div className="flex-1">
                  <p className="font-medium text-emerald-900">Contrat uploadé et converti avec succès</p>
                  <p className="text-xs text-emerald-700">
                    Le nouveau template est actif. Les employés qui n&apos;ont pas encore signé apparaîtront dans l&apos;onglet « Statut des signatures ».
                  </p>
                </div>
              </div>

              {!result.libreoffice_available && (
                <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-orange-800">
                    <strong>LibreOffice non installé sur ce serveur.</strong> Le PDF pixel-perfect n&apos;a pas pu être généré. Seul le rendu HTML éditable a été extrait.
                  </div>
                </div>
              )}

              {result.warnings?.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-yellow-800">
                    <strong>{result.warnings.length} avertissement(s) de conversion :</strong>
                    <ul className="mt-1 space-y-0.5">
                      {result.warnings.slice(0, 5).map((w, i) => <li key={i}>• {w}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {pdfPreviewUrl && (
                <div>
                  <Label className="text-sm font-medium">Prévisualisation PDF pixel-perfect</Label>
                  <p className="text-xs text-slate-500 mb-2">C&apos;est ainsi que le contrat apparaîtra une fois généré. Les variables `{`{{VAR}}`}` seront remplacées lors de la signature.</p>
                  <iframe
                    src={pdfPreviewUrl}
                    className="w-full h-96 border rounded-lg"
                    title="Aperçu PDF"
                    data-testid="upload-pdf-preview"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="upload-cancel">
                Annuler
              </Button>
              <Button
                onClick={upload}
                disabled={!file || uploading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="upload-submit"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? "Conversion en cours…" : "Uploader et convertir"}
              </Button>
            </>
          ) : (
            <Button onClick={validate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-done">
              <CheckCircle className="w-4 h-4 mr-2" /> Continuer vers l&apos;éditeur
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UploadContractDialog;
